// activityGate — is anybody actually using this tab? (Aurora load workstream #4)
//
// Outside business hours WHP has to go quiet for long stretches so the Aurora
// Serverless v2 cluster can auto-pause. The thing keeping it awake all night was
// a staff tab left open: the nav-badge poll, the Overview poll, the lock
// heartbeat, and a WebSocket that API Gateway drops every ~10 min and the page
// dutifully re-opened (ticket + $connect INSERT + $disconnect DELETE + a list
// refetch). Every fixed-interval staff poller now asks this gate first.
//
// A tab is ACTIVE when it is visible AND there was user input (pointerdown,
// keydown, wheel, touchstart, focus; mousemove throttled) within `idleMs`
// (default 15 min). A page load counts as input. Becoming visible does NOT by
// itself count — the window `focus` or the first mouse move that follows does.
//
// Callers can watch with their own thresholds (`ActivityWatch`):
//   idleMs         — how long without input before the tab is idle
//   hiddenGraceMs  — how long the tab may be hidden before it is idle
//                    (0 = the moment it is hidden, the default)
// A watch is active while `now < min(lastInput + idleMs, hiddenAt + hiddenGraceMs)`.
// One shared timer, armed for the earliest deadline among the subscribed
// watches, detects going idle; input does not re-arm it (the timer re-arms
// lazily when it fires), so the input listeners stay O(1).
//
// The core is pure and injectable (clock, timers, event targets) so it runs
// under node:test without a DOM — see activityGate.test.ts. The browser
// singleton is `getActivityGate()`.
//
// Reuse (e.g. the public sign page): `createActivePoller` / the React wrapper
// `usePollWhileActive` (hooks/usePollWhileActive.ts) give "poll on an interval,
// pause while nobody is here, refresh on return" in one call.

// ---------------------------------------------------------------------------
// Thresholds
// ---------------------------------------------------------------------------

/** Default idle threshold: 15 minutes without input. */
export const DEFAULT_IDLE_MS = 15 * 60_000;

/** mousemove fires at frame rate; it only refreshes the input clock this often. */
export const MOUSEMOVE_THROTTLE_MS = 2_000;

/** The input events that count as someone being here (window, capture phase). */
export const ACTIVITY_INPUT_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'focus',
  'mousemove',
] as const;

// ---------------------------------------------------------------------------
// Injectable environment
// ---------------------------------------------------------------------------

type Listener = (event: { type: string }) => void;

/** The slice of `window` / `document` the gate listens on. */
export interface ActivityEventSource {
  addEventListener(
    type: string,
    listener: Listener,
    options?: boolean | { capture?: boolean; passive?: boolean },
  ): void;
  removeEventListener(
    type: string,
    listener: Listener,
    options?: boolean | { capture?: boolean },
  ): void;
}

/** The slice of `document` that reports visibility. */
export interface VisibilitySource extends ActivityEventSource {
  readonly visibilityState: string;
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/** globalThis timers — valid in the browser and under node:test. */
export const defaultTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as number),
};

export interface ActivityWatch {
  /** No input for this long → idle. Default DEFAULT_IDLE_MS. */
  idleMs?: number;
  /** Hidden for this long → idle. Default 0 (hidden = idle at once). */
  hiddenGraceMs?: number;
}

export interface ActivityGate {
  /** Is the tab active under `watch` (default: 15 min idle, hidden = idle)? Computed from the clock, never stale. */
  isActive(watch?: ActivityWatch): boolean;
  /**
   * Called with `false` on active→inactive and `true` on inactive→active for
   * this watch. Transitions only — read `isActive(watch)` for the current value.
   * Returns the unsubscribe function.
   */
  subscribe(listener: (active: boolean) => void, watch?: ActivityWatch): () => void;
  /** Epoch ms of the last counted input (page load counts). */
  lastInputAt(): number;
  /** Detach every DOM listener and timer (tests; the browser singleton never disposes). */
  dispose(): void;
}

export interface ActivityGateDeps {
  now: () => number;
  timers: Timers;
  /** Where input events are heard (window). Listened in the capture phase. */
  inputTarget: ActivityEventSource;
  /** Where visibility is read and `visibilitychange` is heard (document). */
  visibility: VisibilitySource;
}

// ---------------------------------------------------------------------------
// Core
// ---------------------------------------------------------------------------

interface Resolved {
  idleMs: number;
  hiddenGraceMs: number;
}

function resolveWatch(watch: ActivityWatch | undefined): Resolved {
  return {
    idleMs: watch?.idleMs ?? DEFAULT_IDLE_MS,
    hiddenGraceMs: watch?.hiddenGraceMs ?? 0,
  };
}

interface Subscription {
  watch: Resolved;
  listener: (active: boolean) => void;
  active: boolean;
}

export function createActivityGate(deps: ActivityGateDeps): ActivityGate {
  const { now, timers, inputTarget, visibility } = deps;

  let lastInput = now();
  let hiddenAt: number | null = visibility.visibilityState === 'hidden' ? lastInput : null;
  const subs = new Set<Subscription>();
  let timer: unknown = null;
  let timerDue: number | null = null;
  let disposed = false;

  const deadline = (w: Resolved): number => {
    const idleAt = lastInput + w.idleMs;
    return hiddenAt === null ? idleAt : Math.min(idleAt, hiddenAt + w.hiddenGraceMs);
  };

  const clearTimer = () => {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
    timerDue = null;
  };

  // Arm the one timer for the earliest deadline among subscriptions that are
  // currently active (only they can still transition to idle).
  const armTimer = (t: number) => {
    let next = Infinity;
    for (const s of subs) if (s.active) next = Math.min(next, deadline(s.watch));
    if (next === Infinity || disposed) {
      clearTimer();
      return;
    }
    if (timer !== null && timerDue === next) return;
    clearTimer();
    timerDue = next;
    timer = timers.setTimeout(onTimer, Math.max(0, next - t));
  };

  // Re-evaluate every subscription, fire transitions, re-arm the timer.
  const recompute = (t: number) => {
    const changed: Subscription[] = [];
    for (const s of subs) {
      const active = t < deadline(s.watch);
      if (active !== s.active) {
        s.active = active;
        changed.push(s);
      }
    }
    armTimer(t);
    for (const s of changed) {
      if (!subs.has(s)) continue; // unsubscribed by an earlier listener
      try {
        s.listener(s.active);
      } catch (err) {
        console.warn('activityGate.listener.failed', err);
      }
    }
  };

  function onTimer() {
    timer = null;
    timerDue = null;
    if (!disposed) recompute(now());
  }

  const anyInactive = () => {
    for (const s of subs) if (!s.active) return true;
    return false;
  };

  const noteInput = (t: number) => {
    // The idle timer may be overdue (a sleeping laptop, a throttled background
    // tab): flush those idle transitions against the OLD input time first, so a
    // subscriber always sees inactive → active and gets its resume.
    if (timerDue !== null && t >= timerDue) recompute(t);
    lastInput = t;
    // Everyone still active just has a later deadline — the timer re-arms
    // lazily when it fires. Only an inactive watch needs waking now.
    if (anyInactive()) recompute(t);
  };

  const onInput: Listener = (event) => {
    if (disposed) return;
    const t = now();
    if (event.type === 'mousemove' && t - lastInput < MOUSEMOVE_THROTTLE_MS) return;
    noteInput(t);
  };

  const onVisibility: Listener = () => {
    if (disposed) return;
    const t = now();
    if (visibility.visibilityState === 'hidden') {
      if (hiddenAt === null) hiddenAt = t;
    } else {
      hiddenAt = null;
    }
    recompute(t);
  };

  const inputOptions = { capture: true, passive: true };
  for (const type of ACTIVITY_INPUT_EVENTS) inputTarget.addEventListener(type, onInput, inputOptions);
  visibility.addEventListener('visibilitychange', onVisibility);

  return {
    isActive(watch) {
      return now() < deadline(resolveWatch(watch));
    },
    subscribe(listener, watch) {
      const t = now();
      const resolved = resolveWatch(watch);
      const sub: Subscription = { watch: resolved, listener, active: t < deadline(resolved) };
      subs.add(sub);
      armTimer(t);
      return () => {
        subs.delete(sub);
        armTimer(now());
      };
    },
    lastInputAt() {
      return lastInput;
    },
    dispose() {
      disposed = true;
      clearTimer();
      subs.clear();
      for (const type of ACTIVITY_INPUT_EVENTS) {
        inputTarget.removeEventListener(type, onInput, { capture: true });
      }
      visibility.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

// ---------------------------------------------------------------------------
// Browser singleton
// ---------------------------------------------------------------------------

const ALWAYS_ACTIVE: ActivityGate = {
  isActive: () => true,
  subscribe: () => () => {},
  lastInputAt: () => Date.now(),
  dispose: () => {},
};

let shared: ActivityGate | null = null;

/**
 * The page-wide gate (one set of listeners, one timer, for every caller).
 * Outside a browser (SSR, node) it reports always-active so nothing stalls.
 */
export function getActivityGate(): ActivityGate {
  if (shared) return shared;
  if (typeof window === 'undefined' || typeof document === 'undefined') return ALWAYS_ACTIVE;
  shared = createActivityGate({
    now: () => Date.now(),
    timers: defaultTimers,
    inputTarget: window,
    visibility: document,
  });
  return shared;
}

// ---------------------------------------------------------------------------
// Active poller — "poll every N ms, but only while somebody is here"
// ---------------------------------------------------------------------------

export interface ActivePollerOptions {
  gate: ActivityGate;
  /** The poll. Not awaited (setInterval semantics); errors are the caller's to handle. */
  run: () => unknown;
  intervalMs: number;
  /** Run once immediately on start, whatever the activity (a page's initial load). Default true. */
  runOnStart?: boolean;
  watch?: ActivityWatch;
  timers?: Timers;
  now?: () => number;
}

export interface ActivePoller {
  stop(): void;
}

/**
 * Runs `run` every `intervalMs` while the gate is active and not at all while it
 * is inactive. On inactive → active it runs `run` immediately IF a tick came due
 * while paused (exactly once), then resumes the cadence from there; if the pause
 * was shorter than the interval (a quick tab switch) it just resumes the
 * countdown — so an active user sees exactly the old setInterval cadence and a
 * returning one never sees data older than one interval.
 */
export function createActivePoller(opts: ActivePollerOptions): ActivePoller {
  const timers = opts.timers ?? defaultTimers;
  const now = opts.now ?? (() => Date.now());
  const { gate, intervalMs, watch } = opts;

  let lastRunAt = -Infinity;
  let timer: unknown = null;
  let stopped = false;

  const clear = () => {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
  };

  const fire = () => {
    lastRunAt = now();
    try {
      void opts.run();
    } catch (err) {
      console.warn('activePoller.run.failed', err);
    }
  };

  const arm = () => {
    clear();
    if (stopped) return;
    timer = timers.setTimeout(tick, Math.max(0, lastRunAt + intervalMs - now()));
  };

  function tick() {
    timer = null;
    if (stopped) return;
    // The gate's own idle timer may not have fired yet (sleep, throttling):
    // ask the clock. Paused; the gate's next inactive→active re-arms us.
    if (!gate.isActive(watch)) return;
    fire();
    arm();
  }

  const onActivity = (active: boolean) => {
    if (stopped) return;
    if (!active) {
      clear();
      return;
    }
    if (timer !== null) return; // already running
    if (now() - lastRunAt >= intervalMs) fire();
    arm();
  };

  const unsubscribe = gate.subscribe(onActivity, watch);
  if (opts.runOnStart ?? true) fire();
  else lastRunAt = now();
  if (gate.isActive(watch)) arm();

  return {
    stop() {
      stopped = true;
      clear();
      unsubscribe();
    },
  };
}

// ---------------------------------------------------------------------------
// Idle suspender — "stop holding X after a long absence; re-prove it on return"
// ---------------------------------------------------------------------------

export interface IdleSuspenderOptions {
  gate: ActivityGate;
  watch: ActivityWatch;
  /** Is there anything to suspend right now? Checked at the moment the watch goes idle. */
  canSuspend: () => boolean;
  /** The watch went idle while `canSuspend()` — stop the resource. */
  onSuspend: () => void;
  /**
   * Somebody is back while suspended. Called on EVERY inactive→active while
   * suspended (so a failed re-establish is retried on the next return); call
   * `resolve()` once the resource is re-established (or definitively lost).
   */
  onResume: () => void;
}

export interface IdleSuspender {
  readonly suspended: boolean;
  /**
   * Suspend now if the watch is already idle — for a resource (re)started
   * after the idle transition went by (e.g. while `canSuspend()` was false).
   */
  checkNow(): void;
  resolve(): void;
  stop(): void;
}

export function createIdleSuspender(opts: IdleSuspenderOptions): IdleSuspender {
  let suspended = false;
  let stopped = false;
  const trySuspend = () => {
    if (!suspended && opts.canSuspend()) {
      suspended = true;
      opts.onSuspend();
    }
  };
  const unsubscribe = opts.gate.subscribe((active) => {
    if (stopped) return;
    if (!active) {
      trySuspend();
      return;
    }
    if (suspended) opts.onResume();
  }, opts.watch);
  return {
    get suspended() {
      return suspended;
    },
    checkNow() {
      if (!stopped && !opts.gate.isActive(opts.watch)) trySuspend();
    },
    resolve() {
      suspended = false;
    },
    stop() {
      stopped = true;
      unsubscribe();
    },
  };
}

// ---------------------------------------------------------------------------
// Barrier — hold writes until a pending check settles
// ---------------------------------------------------------------------------

export interface Barrier {
  /** Settles (never rejects) with the `release` outcome: true = proceed, false = abandon. */
  readonly wait: Promise<boolean>;
  /** First call wins; later calls are ignored. */
  release(proceed: boolean): void;
}

export function createBarrier(): Barrier {
  let settle!: (proceed: boolean) => void;
  const wait = new Promise<boolean>((resolve) => {
    settle = resolve;
  });
  return { wait, release: (proceed) => settle(proceed) };
}
